import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";

const AgentAddEdit = () => {
  const params = new URLSearchParams(window.location.hash.split("?")[1]);
  const agentIdFromUrl = params.get("id");
  const [showPassword, setShowPassword] = useState(false);

  const isEdit = Boolean(agentIdFromUrl);
  const userId = localStorage.getItem("userId");

  /* ===================== FORM STATE ===================== */
  const [form, setForm] = useState({
    agentName: "",
    gender: "",
    currentAddress: "",
    phoneNumber: "",
    email: "",
    userName: "",
    password: "",
    companyCode: "",
    branchCode: "",
    status: "ACTIVE",
  });

  /* ===================== DROPDOWNS ===================== */
  const [companyList, setCompanyList] = useState([]);
  const [branchList, setBranchList] = useState([]);

  /* ===================== AGENT ID AFTER CREATE ===================== */
  const [createdAgentId, setCreatedAgentId] = useState(null);

  /* ===================== DOCUMENT FILES ===================== */
  const [kycFile, setKycFile] = useState(null);
  const [offerLetterFile, setOfferLetterFile] = useState(null);

  /* ===================== FETCH COMPANIES ===================== */
  const fetchCompanies = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_LIST_BY_CODE);
      setCompanyList(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load companies");
    }
  };

  /* ===================== FETCH BRANCHES ===================== */
  const fetchBranches = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_BRANCH);
      setBranchList(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to load branches");
    }
  };

  /* ===================== FETCH AGENT (EDIT) ===================== */
  const fetchAgent = async () => {
    try {
      const res = await axiosInstance.get(
        `${URL.GET_ALL_AGENT}?id=${agentIdFromUrl}`
      );

      const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      const a = list.find((x) => String(x.id) === String(agentIdFromUrl)) || list[0];
      if (a) {
        setForm({
          agentName: a.agentName || "",
          gender: a.gender || "",
          currentAddress: a.currentAddress || "",
          phoneNumber: a.phoneNumber || "",
          email: a.email || "",
          userName: a.userName || "",
          password: "",
          companyCode: a.companyCode || "",
          branchCode: a.branchCode || "",
          status: a.status || "ACTIVE",
        });
      }
    } catch {
      toast.error("Failed to load agent");
    }
  };

  /* ===================== INIT ===================== */
  useEffect(() => {
    fetchCompanies();
    fetchBranches();
    if (isEdit) fetchAgent();
  }, []);

  /* ===================== HANDLE CHANGE ===================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({
      ...p,
      [name]: value,
      ...(name === "companyCode" ? { branchCode: "" } : {}),
    }));
  };

  /* ===================== ADD / EDIT AGENT ===================== */
  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        companyCode: Number(form.companyCode),
        branchCode: form.branchCode ? Number(form.branchCode) : null,
        createdBy: Number(userId),
      };

     if (isEdit) {
  const res = await axiosInstance.post(
    `${URL.EDIT_AGENT}?agentId=${agentIdFromUrl}`,
    {
      ...payload,
      updatedBy: Number(userId),
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (res.data?.error) {
    toast.error(res.data.error);
    return;
  }

  toast.success(res.data?.message || "Agent updated successfully");
  setTimeout(() => { window.location.href = "#/Agent"; }, 1200);
      } else {
        const res = await axiosInstance.post(URL.ADD_AGENT, payload);

        if (res.data?.error) {
          toast.error(res.data.error);
          return;
        }

        toast.success(res.data?.message || "Agent created");

        if (res.data?.agentId) {
          setCreatedAgentId(res.data.agentId);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Something went wrong");
    }
  };

  /* ===================== UPLOAD DOCUMENTS ===================== */
  const handleUploadDocuments = async () => {
    if (!kycFile && !offerLetterFile) {
      toast.warning("Please select at least one document");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("agentId", createdAgentId);

      if (kycFile) formData.append("kycFile", kycFile);
      if (offerLetterFile)
        formData.append("offerLetterFile", offerLetterFile);

      await axiosInstance.post(URL.ADD_DOCUMENTS_AGENT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Documents uploaded successfully");

      setTimeout(() => {
        window.location.href = "#/Agent";
      }, 1200);
    } catch (err) {
      toast.error("Document upload failed");
    }
  };

  return (
    <div className="container">
      <h3 className="mb-3">{isEdit ? "Edit Agent" : "Add Agent"}</h3>

      {/* ===================== AGENT FORM ===================== */}
      <div className="card p-4 mb-4">
        <div className="row g-3">
          <div className="col-md-4">
            <label>Agent Name</label>
            <input
              className="form-control"
              name="agentName"
              value={form.agentName}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label>Gender</label>
            <select
              className="form-select"
              name="gender"
              value={form.gender}
              onChange={handleChange}
            >
              <option value="">Select</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </div>

          <div className="col-md-4">
            <label>Mobile</label>
            <input
              className="form-control"
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label>Email</label>
            <input
              className="form-control"
              name="email"
              value={form.email}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-4">
            <label>Username</label>
            <input
              className="form-control"
              name="userName"
              value={form.userName}
              onChange={handleChange}
            />
          </div>

          {!isEdit && (
            <div className="col-md-4">
              <label>Password</label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>
          )}

          <div className="col-md-6">
            <label>Address</label>
            <input
              className="form-control"
              name="currentAddress"
              value={form.currentAddress}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-3">
            <label>Company</label>
            <select
              className="form-select"
              name="companyCode"
              value={form.companyCode}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {companyList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.value}
                </option>
              ))}
            </select>
          </div>

          {form.companyCode && (
            <div className="col-md-3">
              <label>Branch</label>
              <select
                className="form-select"
                name="branchCode"
                value={form.branchCode}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {branchList
                  .filter(
                    (b) =>
                      String(b.companyCode) ===
                      String(form.companyCode)
                  )
                  .map((b) => (
                    <option key={b.branchCode} value={b.branchCode}>
                      {b.branchName}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {isEdit && (
            <div className="col-md-3">
              <label>Status</label>
              <select
                className="form-select"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
          )}
        </div>

        <div className="mt-4">
          <button className="btn btn-primary" onClick={handleSubmit}>
            {isEdit ? "Update Agent" : "Save Agent"}
          </button>
        </div>
      </div>

      {/* ===================== DOCUMENT UPLOAD (AFTER ADD) ===================== */}
      {createdAgentId && (
        <div className="card p-4">
          <h5>Upload Agent Documents</h5>

          <div className="row g-3">
            <div className="col-md-6">
              <label>KYC Document</label>
              <input
                type="file"
                className="form-control"
                onChange={(e) => setKycFile(e.target.files[0])}
              />
            </div>

            <div className="col-md-6">
              <label>Offer Letter</label>
              <input
                type="file"
                className="form-control"
                onChange={(e) =>
                  setOfferLetterFile(e.target.files[0])
                }
              />
            </div>
          </div>

          <button
            className="btn btn-success mt-3"
            onClick={handleUploadDocuments}
          >
            Upload Documents
          </button>
        </div>
      )}
        <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
  );
};

export default AgentAddEdit;